import { memo, ReactNode, useCallback } from 'react';
import { useMediaQuery } from '@providers/MediaQueryProvider';

import { BoardThemes } from '@exports/ChessBoard';
import { cx } from '@libs/classnames';
import { Loader } from '@ui/components/Loader';
import { PageComplexSubTitle } from '@ui/components/PageComplexSubTitle';
import { PaginationWithScroll } from '@ui/components/PaginationWithScroll/PaginationWithScroll';
import { Toggle } from '@ui/components/Toggle/Toggle';

import { GameCard, GameCardGame } from '@features/tournament/ui/components/GameCard';

import styles from './TournamentGames.module.css';

export type Game = GameCardGame & {
  whiteId: string;
  blackId: string;
  white2Id?: string;
  black2Id?: string;
};

export type Props = {
  className?: string;
  theme?: BoardThemes;
  roundNumber?: number;
  roundsCount?: number;
  roundRender?: (roundNumber: number) => ReactNode;
  roundsAreStages?: boolean;
  games?: Game[];
  showToggle?: boolean;
  gamesHidden?: boolean;
  loading?: boolean;
  filters?: ReactNode;
  onGameClick?: (gameId: string) => void;
  onRoundClick?: (roundNumber: number) => void;
  onGamesToggle?: (newValue: boolean) => void;
  renderShieldQuickPeek?: (userId: string, className?: string) => ReactNode;
};

export function TournamentGames({
  className,
  theme,
  roundNumber,
  roundsCount,
  roundRender,
  roundsAreStages,
  games,
  showToggle = false,
  gamesHidden,
  loading,
  filters,
  onGameClick,
  onRoundClick,
  onGamesToggle,
  renderShieldQuickPeek,
}: Props) {
  const handleGamesToggle = useCallback(
    (show: boolean) => {
      onGamesToggle?.(!show);
    },
    [onGamesToggle],
  );

  const isMobile = useMediaQuery() === 'mobile';

  const label = isMobile ? 'Партии' : roundsAreStages ? 'Партии раунда' : 'Партии тура';

  const pagination = !gamesHidden && roundNumber && roundsCount && (
    <PaginationWithScroll
      className={styles.paging}
      variant="new"
      pageNum={roundNumber}
      pageTotal={roundsCount}
      pageRender={roundRender}
      maxItems={isMobile ? undefined : 10}
      fullWidth={isMobile ? true : false}
      arrows="scrolling"
      onPage={onRoundClick}
    />
  );

  return (
    <section className={cx(styles.gamesSection, className)}>
      <PageComplexSubTitle
        title={label}
        filters={!isMobile && !gamesHidden ? pagination : undefined}
        actions={
          showToggle ? (
            <Toggle value={!gamesHidden} onToggle={handleGamesToggle} variant="new" />
          ) : undefined
        }
      />
      {isMobile && pagination}
      {!gamesHidden && filters && <div className={styles.filter}>{filters}</div>}
      {!games && loading && <Loader className={styles.loader} />}
      {games && (
        <Games
          theme={theme}
          loading={loading}
          games={games}
          onGameClick={onGameClick}
          hide={!!gamesHidden}
          renderShieldQuickPeek={renderShieldQuickPeek}
        />
      )}
    </section>
  );
}

const Games = memo(function Games({
  theme,
  hide,
  games,
  loading,
  onGameClick,
  renderShieldQuickPeek,
}: {
  theme?: BoardThemes;
  hide: boolean;
  games: Props['games'];
  loading?: boolean;
  onGameClick: Props['onGameClick'];
  renderShieldQuickPeek?: Props['renderShieldQuickPeek'];
}) {
  return (
    <div
      className={cx(styles.games, {
        [styles.hide]: hide,
        [styles.opaque]: loading,
      })}
    >
      {games?.map((game) => {
        const { fen, lastMove } = game;

        return (
          <GameCard
            className={styles.gameCard}
            key={game.id}
            id={game.id}
            theme={theme}
            whiteName={game.whiteName}
            blackName={game.blackName}
            white2Name={game.white2Name}
            black2Name={game.black2Name}
            whiteRating={game.whiteRating}
            blackRating={game.blackRating}
            white2Rating={game.white2Rating}
            black2Rating={game.black2Rating}
            fen={fen}
            whiteTimeMs={game.whiteTimeMs}
            blackTimeMs={game.blackTimeMs}
            ticking={game.ticking}
            winner={game.winner}
            lastMove={lastMove}
            onClick={() => onGameClick?.(game.id)}
            whiteShield={renderShieldQuickPeek?.(game.whiteId)}
            blackShield={renderShieldQuickPeek?.(game.blackId)}
            white2Shield={game.white2Id && renderShieldQuickPeek?.(game.white2Id)}
            black2Shield={game.black2Id && renderShieldQuickPeek?.(game.black2Id)}
          />
        );
      })}
    </div>
  );
});
